import { OverlayAppearancePreferences } from './overlayAppearance';

export type CasesViewMode = 'card' | 'table' | 'gallery';
export type ColorModePreference = 'light' | 'dark' | 'system';

export interface LastStudyPreference {
  labId: string;
  studyId: string;
}

export interface UserPreferences {
  overlayAppearance?: OverlayAppearancePreferences;
  theme?: {
    mode?: ColorModePreference;
  };
  studyDashboard?: {
    casesViewMode?: CasesViewMode;
  };
  nucleaiOrigins?: {
    pinnedAnnotationAssignmentIds?: number[];
  };
  navigation?: {
    lastLabId?: string;
    lastStudy?: LastStudyPreference;
  };
}

export const CASES_VIEW_MODE_STORAGE_KEY = 'userSelectedProcedureViewMode';
export const LAST_SELECTED_LAB_STORAGE_KEY = 'lastSelectedLabId';
export const LAST_SELECTED_STUDY_STORAGE_KEY = 'lastSelectedStudy';

export const normalizeCasesViewMode = (value: unknown): CasesViewMode | null => {
  if (value === 'card' || value === 'table' || value === 'gallery') {
    return value;
  }

  return null;
};

export const normalizeLastStudyPreference = (value: unknown): LastStudyPreference | null => {
  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as LastStudyPreference).labId === 'string' &&
    typeof (value as LastStudyPreference).studyId === 'string'
  ) {
    return {
      labId: (value as LastStudyPreference).labId,
      studyId: (value as LastStudyPreference).studyId,
    };
  }

  return null;
};

export const getStoredPreferenceValue = (storageKey: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(storageKey);
};

export const hasStoredPreferenceValue = (storageKey: string) => getStoredPreferenceValue(storageKey) !== null;

export const setStoredPreferenceValue = (storageKey: string, value: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(storageKey, value);
};

export const getStoredJsonPreference = <T>(storageKey: string): T | null => {
  const value = getStoredPreferenceValue(storageKey);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

export const setStoredJsonPreference = (storageKey: string, value: unknown) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(storageKey, JSON.stringify(value));
};

export const getCasesViewModeFromUserPreferences = (preferences: UserPreferences) =>
  normalizeCasesViewMode(preferences?.studyDashboard?.casesViewMode);

export const buildCasesViewModePreferencesPatch = (casesViewMode: CasesViewMode): Partial<UserPreferences> => ({
  studyDashboard: {
    casesViewMode,
  },
});

export const getLastLabIdFromUserPreferences = (preferences: UserPreferences) =>
  preferences?.navigation?.lastLabId || null;

export const buildLastLabPreferencesPatch = (lastLabId: string): Partial<UserPreferences> => ({
  navigation: {
    lastLabId,
  },
});

export const getLastStudyFromUserPreferences = (preferences: UserPreferences) =>
  normalizeLastStudyPreference(preferences?.navigation?.lastStudy);

export const buildLastStudyPreferencesPatch = (lastStudy: LastStudyPreference): Partial<UserPreferences> => ({
  navigation: {
    lastStudy,
  },
});

export const normalizePinnedAnnotationAssignmentIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((annotationAssignmentId): annotationAssignmentId is number =>
    Number.isInteger(annotationAssignmentId)
  );
};

export const getPinnedAnnotationAssignmentIdsFromUserPreferences = (preferences: UserPreferences) =>
  normalizePinnedAnnotationAssignmentIds(preferences?.nucleaiOrigins?.pinnedAnnotationAssignmentIds);

export const buildPinnedAnnotationAssignmentsPreferencesPatch = (
  pinnedAnnotationAssignmentIds: number[]
): Partial<UserPreferences> => ({
  nucleaiOrigins: {
    pinnedAnnotationAssignmentIds,
  },
});

export const getStoredLastLabId = () => getStoredPreferenceValue(LAST_SELECTED_LAB_STORAGE_KEY);

export const setStoredLastLabId = (lastLabId: string) =>
  setStoredPreferenceValue(LAST_SELECTED_LAB_STORAGE_KEY, lastLabId);

export const getStoredLastStudyPreference = () =>
  normalizeLastStudyPreference(getStoredJsonPreference<LastStudyPreference>(LAST_SELECTED_STUDY_STORAGE_KEY));

export const setStoredLastStudyPreference = (lastStudy: LastStudyPreference) =>
  setStoredJsonPreference(LAST_SELECTED_STUDY_STORAGE_KEY, lastStudy);
